import { useState } from "react";
import { Button } from "@common/index";
import { ComponentColor } from "@common/types";
import {
    useAcceptJs,
    type AcceptJsAccountType,
    type AcceptJsBankCapture,
} from "@/hooks/useAcceptJs";
import { authNetConfig } from "@/config/paymentConfig";

// Drydock harness for the Accept.js bank-account tokenization step (#296). It proves, in
// isolation, that VITE_AUTHNET_ACCEPT_URL + the public keys are wired correctly and that
// v1/Accept.js can turn bank details into an opaque nonce WITHOUT going through
// cart/checkout/auth. The details go browser → Authorize.Net directly; this page only ever
// sees the nonce plus the last4 the hook computed on our side. Tokenization does not move
// money — the nonce is only charged when the API spends it. Run over the HTTPS dev server
// with SANDBOX values (e.g. routing 021000021, any 4–17 digit account number).
//
// Deliberately plain local-state inputs, not react-hook-form/zod: the checkout PaymentForm
// owns the validated customer surface; this panel exercises the HOOK in isolation, so the
// vendor's own rejections (bad routing number etc.) stay visible instead of being
// pre-filtered.
export const BankTokenization = () => {
    const { isConfigured, tokenizeBankAccount } = useAcceptJs();

    const [routingNumber, setRoutingNumber] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [nameOnAccount, setNameOnAccount] = useState("");
    const [accountType, setAccountType] =
        useState<AcceptJsAccountType>("checking");
    const [capture, setCapture] = useState<AcceptJsBankCapture | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleTokenize = async () => {
        setError(null);
        try {
            setCapture(
                await tokenizeBankAccount({
                    accountNumber,
                    routingNumber,
                    nameOnAccount,
                    accountType,
                }),
            );
            // Same discipline as checkout's form: the full account number must not
            // outlive the vendor call. Routing/name stay for re-run convenience.
            setAccountNumber("");
        } catch (failure) {
            setCapture(null);
            setError(
                failure instanceof Error ? failure.message : String(failure),
            );
        }
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md max-w-4xl">
            <h2 className="text-xl font-bold mb-1">
                Authorize.Net Bank Account Tokenization (Accept.js)
            </h2>
            <p className="text-sm mb-4">
                Environment: <b>{authNetConfig.environment}</b> · Configured:{" "}
                <b>
                    {isConfigured
                        ? "yes"
                        : "no (set VITE_AUTHNET_* env vars incl. VITE_AUTHNET_ACCEPT_URL)"}
                </b>
            </p>
            <p className="text-sm mb-4">
                Sends the entered bank details straight to Authorize.Net via
                Accept.js — they never reach our API. Submitting hands back
                only the opaque nonce below.
            </p>

            <div className="flex flex-col gap-2 max-w-md mb-4">
                <input
                    className="input input-bordered w-full text-black"
                    placeholder="Routing number"
                    value={routingNumber}
                    onChange={(e) => setRoutingNumber(e.target.value)}
                />
                <input
                    className="input input-bordered w-full text-black"
                    placeholder="Account number"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                />
                <input
                    className="input input-bordered w-full text-black"
                    placeholder="Name on account"
                    value={nameOnAccount}
                    onChange={(e) => setNameOnAccount(e.target.value)}
                />
                <select
                    aria-label="Account type"
                    className="select select-bordered w-full text-black"
                    value={accountType}
                    onChange={(e) =>
                        setAccountType(e.target.value as AcceptJsAccountType)
                    }
                >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                    <option value="businessChecking">Business checking</option>
                </select>
            </div>

            <Button
                onClick={() => void handleTokenize()}
                color={ComponentColor.Primary}
                disabled={!isConfigured}
            >
                Tokenize bank account
            </Button>

            {capture && (
                <div className="mt-4 border-t pt-4">
                    <h3 className="font-semibold text-success mb-1">
                        ✓ Nonce received (vendorPaymentToken)
                    </h3>
                    <textarea
                        readOnly
                        className="textarea textarea-bordered w-full text-xs font-mono"
                        rows={3}
                        value={capture.token}
                    />
                    <p className="text-xs mt-1">
                        Masked account: <b>{`••••${capture.accountLast4}`}</b>
                    </p>
                    <p className="text-xs mt-1">
                        Single-use, expires in ~15 min. This is the exact value
                        the checkout sends to <code>POST /orders</code> (with
                        paymentMethod 2).
                    </p>
                </div>
            )}

            {error && (
                <div className="mt-4 border-t pt-4">
                    <h3 className="font-semibold text-error mb-1">
                        ✗ Bank tokenization failed
                    </h3>
                    <p className="text-sm">{error}</p>
                </div>
            )}
        </div>
    );
};
